// dependencies
import {
	CategoryScale,
	Chart,
	Chart as ChartJS,
	LineElement,
	LinearScale,
	PointElement,
	Title,
	Tooltip,
} from "chart.js";
import { startOfMonth } from "date-fns";
import React, { MutableRefObject, useEffect, useRef, useState } from "react";
import { Line, getElementAtEvent } from "react-chartjs-2";
import { useNavigate } from "react-router-dom";

import EmptyChart from "../../../../../../components/EmptyChart";
import Spinner from "../../../../../../components/Spinner/QuarterCircle";
import Text from "../../../../../../components/Text";
// components
import { CardBody, CardHeader } from "../../../../components/Card";
// styles
import css from "../../../../components/SatisfactionTrendlineChart/SatisfactionTrendline.module.scss";
// hooks
import useOverallSatisfaction from "../../../../components/SatisfactionTrendlineChart/useSatisfactionTrendlineChart";

// store
import useStore from "../../../../store";

// config
import Config from "../../../../Config";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip);

const OverallSatisfaction = () => {
	const [filter, month, cohort, setMonth, setCohort] = useStore((state) => [
		state.filter,
		state.overview.month,
		state.overview.cohort,
		state.overview.setMonth,
		state.overview.setCohort,
	]);
	const [initialFilterValueSet, setInitialFilterValueSet] = useState<boolean>(false);
	const navigate = useNavigate();

	// chart ref
	const chartRef = useRef<ChartJS>();

	// clear selectedDataPoint value when filter changes
	useEffect(() => {
		if (!initialFilterValueSet && filter.value) {
			// setInitialFilterValue(filter.value);
			setInitialFilterValueSet(true);
			// if setting the initial filter value, don't clear the selected data point
			return;
		}
		// if (initialFilterValue === filter.value) return;
		setMonth(null);
		setCohort("all");
	}, [filter.value]);

	// get data for line chart
	const { data, legendItems, options, dateRangeLabels, isLoading, indexOfSelectedMonth, indexOfSelectedCohort } =
		useOverallSatisfaction(filter, month, cohort || "");

	let hasSufficientData = false;

	// check if chart has sufficient data
	if (data.datasets[0].data && data.datasets[0].data.length) {
		const dataSetData = data.datasets[0].data;
		// data exists, set sufficient to true
		if (dataSetData.length) {
			hasSufficientData = true;
		}
	}

	// handles clicking on a data point on the line chart
	const onClick = (event) => {
		if (chartRef.current) {
			const monthClicked = getElementAtEvent(chartRef.current, event)[0]?.index;

			if (typeof monthClicked === "number") {
				const dataSet = getElementAtEvent(chartRef.current, event)[0]?.datasetIndex;

				if (monthClicked === indexOfSelectedMonth && dataSet === indexOfSelectedCohort) {
					setMonth(null);
					setCohort("all");
				} else {
					// format date label to be a date object for store
					// defaults to first day of the month (year-month-1)
					const formattedMonth = startOfMonth(new Date(`${dateRangeLabels[monthClicked].value}-1`));

					setMonth(formattedMonth);
					setCohort("all");
				}
			}
		}
	};

	return (
		<>
			<CardHeader
				title={
					<span>
						<Text element="span" size="paragraph-2" weight="semi-bold">
							Overall Satisfaction
						</Text>
					</span>
				}
				info="This chart displays the overall satisfaction of all employees during  the selected period."
				options={[
					{
						id: "view-factors",
						label: "Factors",
						action: () => {
							// navigate to /engagement/factors page
							navigate(Config.ROUTES.FACTORS.url);
						},
					},
				]}
			/>
			<CardBody>
				{isLoading && (
					<div className={css.cardBody}>
						<div className={css.spinnerContainer}>
							<Spinner />
						</div>
					</div>
				)}
				{!isLoading && !hasSufficientData && <EmptyChart type="trendline" />}

				{!isLoading && hasSufficientData && (
					<div className={css.cardBody}>
						<div className={css.trendlineChartContainer}>
							<div className={`${css.trendChart}`}>
								<Line
									ref={chartRef as MutableRefObject<Chart<"line", any, unknown>>}
									options={options as any}
									data={data}
									onClick={onClick}
								/>
							</div>
							<div className={css.legend}>
								{legendItems.map((item) => (
									<div
										key={item.id}
										style={{
											color: item.color as string,
											backgroundColor: item.backgroundColor as string,
										}}
										className={css.legendLabel}
									>
										<Text
											className={css.legendText}
											element="span"
											size="extra-small"
											weight="semi-bold"
										>
											{item.label}
										</Text>
									</div>
								))}
							</div>
						</div>
					</div>
				)}
			</CardBody>
		</>
	);
};

export default OverallSatisfaction;
